import { ConfigField, ConfigSlideId, Slide } from '@/eventConfig/types.ts';
import { ChangeEventHandler, CSSProperties, FC, KeyboardEventHandler, MouseEventHandler, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { decorate } from '@/form/fields/_pipeline/decorate.ts';
import { normalize } from '@/form/fields/_pipeline/normalize.ts';
import {
  FieldType,
  FieldValue,
  FormGetFieldStatusFn,
  FormGetFieldValueFn,
  FormOnChangeFn,
} from '@/form/FormProvider.tsx';
import { isBmiSlide } from '@/form/utils';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { useFieldValidation } from '../_pipeline/useValidateField';
import { useBmiValidation } from '@/util/useBmiValidation';
import { useRevalidateField } from '@/util/useRevalidateField';
import useSlideIcons from '@/util/slideIcons';
import { decodeZaIdBirthdate, isBirthdateWithinAgeRange } from '@/util/decodeZaIdBirthdate.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { isNil } from 'ramda';

interface TextInputProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const TextInput: FC<TextInputProps> = ({ field, slideId, onChange, getFieldStatus, getFieldValue }) => {
  const fieldStatus = getFieldStatus(field.name);
  const { handleUiEvent } = useUiEvent();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { isValidBmi } = useBmiValidation(getFieldValue);
  const { revalidateFields } = useRevalidateField(validateField);
  const { showLayer } = useLayer();
  const { infoIcon } = useSlideIcons({} as Slide);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');
  const placeholder = translate(field.placeholder);
  const socialMediaHandles = field.options ?? [];
  const hasLink = !isNil(field.link);
  let linkedValue: FieldType | undefined;
  let isHidden = false;

  if (hasLink) {
    const [_slideId, fieldName] = (field.link?.field ?? '').split('.');
    linkedValue = getFieldStatus(fieldName)?.value?.normalized;

    if (socialMediaHandles.length === 0) {
      isHidden = !linkedValue; // Hide if linkedValue is falsy
    } else {
      const handleRelatingToLinkedFieldValue = socialMediaHandles.find(({ label }) => {
        return label === linkedValue;
      });

      if (!handleRelatingToLinkedFieldValue || !handleRelatingToLinkedFieldValue?.options) {
        isHidden = true;
      }

      if ((handleRelatingToLinkedFieldValue?.options || []).length < 2) {
        isHidden = true;
      }
    }
  }
  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  const hasInfoLayer = !!field.info;
  const handleInfoClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    handleUiEvent({
      type: 'layer',
      text: field?.info?.text,
      title: field?.info?.title,
    });
  };

  const infoIconStyles: CSSProperties = {
    position: 'unset',
    marginRight: '0.5rem',
    cursor: 'pointer',
  };

  useEffect(() => {
    const e = { target: { value: value } } as React.ChangeEvent<HTMLInputElement>;
    if (isBmiSlide(slideId)) handleChange(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidBmi, slideId, value]);

  const syncBirthdateFromZaId = (zaIdValue: FieldValue, isZaIdValid: boolean) => {
    if (field.name !== 'za_id') {
      return;
    }

    // Require users to reconfirm derived birthdate whenever the ID changes.
    onChange('birthdateConfirmed', null);
    revalidateFields([{ slideId: 'name', field: 'salutation' }]);

    if (!isZaIdValid) {
      onChange('birthdate', null);
      return;
    }

    const derivedBirthdate = decodeZaIdBirthdate(String(zaIdValue.normalized ?? zaIdValue.raw));
    if (!derivedBirthdate) {
      onChange('birthdate', null);
      return;
    }

    if (!isBirthdateWithinAgeRange(derivedBirthdate, 17, 55)) {
      onChange('birthdate', null);
      onChange(field.name, {
        value: zaIdValue,
        isValid: false,
        hint: { en: 'If you are not between 17 and 55 years old, unfortunately you cannot register.' },
        slideId,
      });
      showLayer({ ref: '@ageHint' });
      return;
    }

    onChange('birthdate', {
      value: {
        raw: derivedBirthdate,
        normalized: normalize('date:DD.MM.YYYY', derivedBirthdate),
      },
      isValid: true,
      hint: undefined,
      slideId: 'birthdate',
    });
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const raw = e.target.value;
    const normalized = normalize(field.datatype, raw);
    const value = { raw: raw, normalized };

    const { hint, isValid } = validateField(field.validator, value);

    onChange(field.name, { value, hint, isValid, slideId });
    syncBirthdateFromZaId(value, isValid);
  };

  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!field.decorator) {
      return;
    }
    const value = e.currentTarget.value;
    if (['Enter', 'Backspace', 'Delete'].indexOf(e.key) < 0) {
      const decorated = decorate(field.decorator, value);
      const normalized = normalize(field.datatype, decorated);
      const fieldValue = { raw: decorated, normalized };
      const { hint, isValid } = validateField(field.validator, fieldValue);

      onChange(field.name, { value: fieldValue, hint, isValid, slideId });
      syncBirthdateFromZaId(fieldValue, isValid);
    }

    if (field.revalidate) revalidateFields(field.revalidate);
  };

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
    isHidden ? 'd-none' : '',
  ];

  const hint = fromTemplate(fieldHint || field.hint || '');

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <label htmlFor={htmlId} className={isRequired ? 'required' : ''} onClick={handleLabelClick}>
          {hasInfoLayer && (
            <span onClick={handleInfoClick}>
              <i className={infoIcon} style={infoIconStyles} />
            </span>
          )}
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>
        <input
          autoComplete={field.autocomplete ? field.autocomplete : 'on'}
          type="text"
          value={value}
          name={field.name}
          placeholder={placeholder}
          id={htmlId}
          maxLength={100}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
        />
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
